import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload, Image, Search, ArrowUpDown, ArrowUp, ArrowDown, Download, ExternalLink, GripVertical, ListOrdered, X, Check, ChevronDown, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { uploadFile } from '@/lib/utils';
import VideoBriefs, { Brief, validateBriefs } from '@/components/VideoBriefs';
import { logCreate, logUpdate, logDelete, logAdminAction } from '@/lib/audit-log';
import { DataPagination } from '@/components/DataPagination';

const emptyCampaign = {
  brand_name: '',
  product_name: '',
  product_id: '',
  tap_link: '',
  product_image_url: '',
  description: '',
  prize_pool: 0,
  total_video_slots: 250,
  status: 'active',
  cash_prizes: [] as { position: number; amount: string }[],
  commission: '',
  gems_coin: '',
  min_videos: 5,
  end_date: '',
  is_hybrid: false,
  guaranteed_payment: '',
  is_bmc_max: false,
  max_required_videos: '',
  gmv_ranges: [] as string[],
  genders: [] as string[],
  additional_image_urls: [] as string[],
};

const MAX_ADDITIONAL_IMAGES = 4;

const GMV_RANGES = [
  { key: 'lt_2500',      label: 'Less than $2,500' },
  { key: '2500_5000',    label: '$2,500 – $5,000' },
  { key: '5001_10000',   label: '$5,001 – $10,000' },
  { key: '10001_20000',  label: '$10,001 – $20,000' },
  { key: '20001_50000',  label: '$20,001 – $50,000' },
  { key: '50001_100000', label: '$50,001 – $100,000' },
  { key: 'gt_100000',    label: 'More than $100,000' },
] as const;

// Gender targeting options. Keys match the gender strings stored on
// profiles.gender / roster_creators.gender so targeting lines up with real
// creator data.
const GENDER_OPTIONS = [
  { key: 'Male',   label: 'Male' },
  { key: 'Female', label: 'Female' },
  { key: 'Others', label: 'Others' },
] as const;

type CampaignType = 'reward_only' | 'hybrid' | 'bmc_max';

// Campaign type selector options. Colors match the table/detail badges:
// Reward Only = blue, Hybrid = emerald, BMC Max = purple.
const CAMPAIGN_TYPES: {
  key: CampaignType; label: string; desc: string; dot: string; activeClass: string;
}[] = [
  {
    key: 'reward_only', label: 'Reward Only', desc: 'Prize pool + commission',
    dot: 'bg-blue-600',
    activeClass: 'border-blue-600 ring-1 ring-blue-600 bg-blue-50 dark:bg-blue-950/20',
  },
  {
    key: 'hybrid', label: 'Hybrid', desc: 'Reward pool + guaranteed pay',
    dot: 'bg-emerald-600',
    activeClass: 'border-emerald-600 ring-1 ring-emerald-600 bg-emerald-50 dark:bg-emerald-950/20',
  },
  {
    key: 'bmc_max', label: 'Pay Per 10 Posts', desc: 'Fixed reward, no pool',
    dot: 'bg-purple-600',
    activeClass: 'border-purple-600 ring-1 ring-purple-600 bg-purple-50 dark:bg-purple-950/20',
  },
];

// Reusable targeting multi-select. Shared by the GMV-range and gender pickers
// in the Hybrid and BMC Max panels so the popover markup isn't duplicated.
// `noun` is used in the trigger copy, e.g. "GMV ranges" / "genders".
function TargetMultiSelect({
  options, value, onChange, noun,
}: {
  options: readonly { key: string; label: string }[];
  value: string[];
  onChange: (next: string[]) => void;
  noun: string;
}) {
  const allKeys = options.map(o => o.key);
  const allSelected = allKeys.every(k => value.includes(k));
  const someSelected = !allSelected && value.length > 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <span className={!value.length ? 'text-muted-foreground' : ''}>
            {value.length === options.length
              ? `All ${noun} selected`
              : value.length
              ? `${value.length} selected`
              : `Select ${noun}...`}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="space-y-1">
          <label className="flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer hover:bg-muted text-sm font-medium border-b pb-2 mb-1">
            <Checkbox
              checked={allSelected}
              data-state={someSelected ? 'indeterminate' : allSelected ? 'checked' : 'unchecked'}
              onCheckedChange={() => onChange(allSelected ? [] : allKeys)}
            />
            All
          </label>
          {options.map(opt => {
            const selected = value.includes(opt.key);
            return (
              <label
                key={opt.key}
                className="flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer hover:bg-muted text-sm"
              >
                <Checkbox
                  checked={selected}
                  onCheckedChange={checked => {
                    onChange(checked ? [...value, opt.key] : value.filter(k => k !== opt.key));
                  }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
        {value.length > 0 && (
          <button
            type="button"
            className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground text-center"
            onClick={() => onChange([])}
          >
            Clear selection
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

// Column configuration with display labels and database field mapping
const COLUMN_CONFIG = [
  { label: 'Brand', field: 'brand_name', sortable: true },
  { label: 'Product', field: 'product_name', sortable: true },
  { label: 'Type', field: 'type', sortable: false },
  { label: 'Prize Pool', field: 'prize_pool', sortable: true },
  { label: 'Commission', field: 'commission', sortable: true },
  // { label: 'Gems', field: 'gems_coin', sortable: true },
  { label: 'Slots', field: 'total_video_slots', sortable: true },
  { label: 'Min Videos', field: 'min_videos', sortable: true },
  { label: 'Flat Fee (10 Videos)', field: 'guaranteed_payment', sortable: true },
  { label: 'Deadline', field: 'end_date', sortable: true },
  { label: 'GMV Ranges', field: 'gmv_ranges', sortable: false },
  { label: 'Genders', field: 'genders', sortable: false },
  { label: 'Status', field: 'status', sortable: false },
  { label: 'Actions', field: 'actions', sortable: false },
];

export default function CreatorCampaigns() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<any | null>(null);
  const [viewing, setViewing] = useState<any | null>(null);
  const [viewBriefs, setViewBriefs] = useState<Brief[]>([]);
  const originalProductId = useRef<string | null>(null);
  const originalCampaignType = useRef<CampaignType | null>(null);
  const saveInFlight = useRef(false);
  const [typeCheckLoading, setTypeCheckLoading] = useState(false);
  const [briefs, setBriefs] = useState<Brief[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [uploadingExtraPhoto, setUploadingExtraPhoto] = useState(false);
  const extraPhotoInputRef = useRef<HTMLInputElement>(null);
  
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [statusTab, setStatusTab] = useState<'all' | 'active' | 'paused' | 'completed'>('all');

  // Reorder mode
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderList, setReorderList] = useState<any[]>([]);
  const [isLoadingReorder, setIsLoadingReorder] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const originalReorderList = useRef<any[]>([]);

  useEffect(() => {
    if (!editing?.id) {
      setBriefs([]);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('campaign_briefs')
        .select('*')
        .eq('campaign_id', editing.id)
        .order('sort_order', { ascending: true });
      setBriefs(
        (data ?? []).map((b: any) => ({
          id: b.id,
          campaign_id: b.campaign_id,
          brief_type: b.brief_type,
          title: b.title || '',
          description: b.description || '',
          thumbnail_url: b.thumbnail_url || '',
          example_video_url: b.example_video_url || '',
          sort_order: b.sort_order,
        }))
      );
    })();
  }, [editing?.id]);

  useEffect(() => {
    if (!viewing?.id) {
      setViewBriefs([]);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('campaign_briefs')
        .select('*')
        .eq('campaign_id', viewing.id)
        .order('sort_order', { ascending: true });
      setViewBriefs(
        (data ?? []).map((b: any) => ({
          id: b.id,
          campaign_id: b.campaign_id,
          brief_type: b.brief_type,
          title: b.title || '',
          description: b.description || '',
          thumbnail_url: b.thumbnail_url || '',
          example_video_url: b.example_video_url || '',
          sort_order: b.sort_order,
        }))
      );
    })();
  }, [viewing?.id]);

  const uploadAsset = async (file: File, bucket: string, prefix: string): Promise<string> => {
    const fileName = `${prefix}-${Date.now()}.${file.name.split('.').pop()}`;
    return await uploadFile(file, bucket, fileName);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setUploadingPhoto(true);
    try {
      const url = await uploadAsset(file, 'campaign-assets', 'product');
      setEditing({ ...editing, product_image_url: url });
      toast.success('Product photo uploaded');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleExtraPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    if ((editing.additional_image_urls?.length ?? 0) >= MAX_ADDITIONAL_IMAGES) return;
    setUploadingExtraPhoto(true);
    try {
      const url = await uploadAsset(file, 'campaign-assets', 'product-extra');
      setEditing({ ...editing, additional_image_urls: [...(editing.additional_image_urls ?? []), url] });
      toast.success('Image uploaded');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploadingExtraPhoto(false);
      if (extraPhotoInputRef.current) extraPhotoInputRef.current.value = '';
    }
  };

  const removeExtraImage = (idx: number) => {
    if (!editing) return;
    setEditing({ ...editing, additional_image_urls: (editing.additional_image_urls ?? []).filter((_: string, i: number) => i !== idx) });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['campaigns', currentPage, pageSize, search, sortConfig?.key, sortConfig?.direction, statusTab],
    queryFn: async () => {
      let query = supabase
        .from('campaigns')
        .select('*', { count: 'exact' });

      if (statusTab !== 'all') {
        query = query.eq('status', statusTab);
      }

      if (search && search.trim() != '') {
        query = query.or(`brand_name.ilike.%${search}%,product_name.ilike.%${search}%`);
      }

      if (sortConfig) {
        query = query.order(sortConfig.key, {
          ascending: sortConfig.direction === 'asc',
        });
      } else {
        query = query
          .order('status', { ascending: true })
          .order('sort_order', { ascending: true, nullsFirst: false });
      }

      query = query.range(
        (currentPage - 1) * pageSize,
        currentPage * pageSize - 1
      );

      const { data, count, error } = await query;

      if (error) throw error;

      return {
        data: data ?? [],
        total: count ?? 0,
      };
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ campaign, briefsToSave }: { campaign: any; briefsToSave: Brief[] }) => {
      const payload = { ...campaign };
      payload.prize_pool = payload.prize_pool ? Number(payload.prize_pool) : 0;
      payload.total_video_slots = payload.total_video_slots ? Number(payload.total_video_slots) : 250;
      // cash_prizes: store as [{position, amount}], filter out incomplete entries
      payload.cash_prizes = Array.isArray(payload?.cash_prizes)
        ? payload?.cash_prizes
            .map((p: any) => ({ position: Number(p?.position), amount: Number(p?.amount) }))
            .filter((p: any) => p.position > 0 && p.amount > 0)
        : [];
      // Keep legacy columns in sync (sorted by position)
      const sortedPrizes = [...payload.cash_prizes].sort((a: any, b: any) => a.position - b.position);
      payload['1st_prize'] = sortedPrizes[0]?.amount ?? null;
      payload['2nd_prize'] = sortedPrizes[1]?.amount ?? null;
      payload['3rd_prize'] = sortedPrizes[2]?.amount ?? null;
      payload.commission = payload.commission ? Number(payload.commission) : null;
      payload.gems_coin = payload.gems_coin ? Number(payload.gems_coin) : null;
      payload.min_videos = payload.min_videos != null && payload.min_videos !== '' ? Number(payload.min_videos) : 5;
      payload.is_hybrid = !!payload.is_hybrid;
      payload.is_bmc_max = !!payload.is_bmc_max;
      // Mutually exclusive: if one is set the other must be false
      if (payload.is_hybrid) payload.is_bmc_max = false;
      if (payload.is_bmc_max) payload.is_hybrid = false;
      payload.guaranteed_payment = (payload.is_hybrid || payload.is_bmc_max) && payload.guaranteed_payment ? Number(payload.guaranteed_payment) : null;
      payload.max_required_videos = payload.is_bmc_max && payload.max_required_videos ? Number(payload.max_required_videos) : null;
      payload.gmv_ranges = (payload.is_hybrid || payload.is_bmc_max) && Array.isArray(payload.gmv_ranges) && payload.gmv_ranges.length > 0 ? payload.gmv_ranges : null;
      payload.genders = (payload.is_hybrid || payload.is_bmc_max) && Array.isArray(payload.genders) && payload.genders.length > 0 ? payload.genders : null;
      payload.additional_image_urls = Array.isArray(payload.additional_image_urls)
        ? payload.additional_image_urls.filter((u: any) => typeof u === 'string' && u.trim())
        : [];
      // BMC Max doesn't use prize pool, cash prizes, commission, or min_videos
      if (payload.is_bmc_max) {
        payload.prize_pool = 0;
        payload.cash_prizes = [];
        payload['1st_prize'] = null;
        payload['2nd_prize'] = null;
        payload['3rd_prize'] = null;
        payload.commission = null;
        payload.min_videos = null;
        payload.total_video_slots = null;
      }
      // store full array even when all ranges selected — null means no restriction (empty dropdown)

      const id = payload.id;
      delete payload.id;
      if (id) delete payload.product_id;
      // delete payload.created_at;
      delete payload.updated_at;
      // delete payload.videos_submitted;
      // delete payload.perks;
      delete payload.commission_rate;
      delete payload.product_photo_url;
      delete payload.hero_image_url;
      delete payload.start_date;
      delete payload.is_visible;
      // Normalize end_date: empty string → null
      if (!payload.end_date) payload.end_date = null;
      delete payload.campaign_brief;
      delete payload.brand_logo_url;

      let oldData = null;
      if (id) {
        const { data: existing } = await supabase.from('campaigns').select('*').eq('id', id).single();
        oldData = existing;
      }

      let campaignId = id;
      if (id) {
        const { error } = await supabase.from('campaigns').update(payload).eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('campaigns').insert(payload);
        if (error) throw error;
        const { data: inserted } = await supabase
          .from('campaigns')
          .select('id')
          .eq('brand_name', payload.brand_name)
          .eq('product_name', payload.product_name)
          .order('created_at', { ascending: false })
          .limit(1);
        campaignId = inserted?.[0]?.id;
      }

      if (!campaignId) throw new Error('Failed to resolve campaign ID');

      await supabase.from('campaign_briefs').delete().eq('campaign_id', campaignId);

      if (briefsToSave?.length > 0) {
        const rows = briefsToSave?.map((b, i) => ({
          campaign_id: campaignId,
          brief_type: b.brief_type,
          title: b.brief_type === 'open' ? null : b.title,
          description: b.description || null,
          thumbnail_url: b.brief_type === 'open' ? null : b.thumbnail_url,
          example_video_url: b.brief_type === 'open' ? null : b.example_video_url,
          sort_order: i,
        }));
        const { error: briefErr } = await supabase.from('campaign_briefs').insert(rows);
        if (briefErr) throw briefErr;
      }

      // Audit log
      const finalData = { ...payload, id: campaignId };
      const metadata = {
        brand_name: payload.brand_name,
        product_name: payload.product_name,
        brief_count: briefsToSave?.length
      };
      if (oldData) {
        await logUpdate('campaigns', campaignId, oldData, finalData, {
          description: `Updated campaign "${payload.product_name}"`,
          metadata
        });
      } else {
        await logCreate('campaigns', campaignId, finalData, {
          description: `Created campaign "${payload.product_name}"`,
          metadata
        });
      }
    },
    onSuccess: () => {
      saveInFlight.current = false;
      toast.success('Campaign saved');
      setEditing(null);
      setBriefs([]);
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err: any) => {
      saveInFlight.current = false;
      toast.error(err.message);
    },
  });

  // ─── Reorder handlers ─────────────────────────────────────────────────────

  const enterReorderMode = async () => {
    setIsLoadingReorder(true);
    try {
      const { data: all, error } = await supabase
        .from('campaigns')
        .select('id, brand_name, product_name, product_image_url, status, sort_order, is_hybrid, is_bmc_max')
        .eq('status', 'active')
        .order('sort_order', { ascending: true, nullsFirst: false });
      if (error) throw error;
      originalReorderList.current = all ?? [];
      setReorderList(all ?? []);
      setReorderMode(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load campaigns');
    } finally {
      setIsLoadingReorder(false);
    }
  };

  const handleDragStart = (index: number) => {
    dragItem.current = index;
    setDraggingIndex(index);
  };
  const handleDragEnter = (index: number) => {
    dragOverItem.current = index;
  };
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current !== dragOverItem.current) {
      const list = [...reorderList];
      const [moved] = list.splice(dragItem.current, 1);
      list.splice(dragOverItem.current, 0, moved);
      setReorderList(list);
    }
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingIndex(null);
  };

  const saveOrderMutation = useMutation({
    mutationFn: async () => {
      await Promise.all(
        reorderList?.map((c, i) =>
          supabase.from('campaigns').update({ sort_order: i + 1 }).eq('id', c.id)
        )
      );
      const oldPositionMap = new Map(
        originalReorderList.current.map((c, i) => [c.id, i + 1])
      );
      const oldValues: Record<string, number> = {};
      const newValues: Record<string, number> = {};
      reorderList.forEach((c, i) => {
        const key = `${c.brand_name} – ${c.product_name} (${c.id})`;
        oldValues[key] = oldPositionMap.get(c.id);
        newValues[key] = i + 1;
      });
      await logAdminAction({
        action: 'UPDATE',
        tableName: 'campaigns',
        oldValues,
        newValues,
        metadata: { operation: 'reorder' },
      });
    },
    onSuccess: () => {
      toast.success('Campaign order saved');
      setReorderMode(false);
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  // ──────────────────────────────────────────────────────────────────────────

  const validateCampaign = (campaign: any): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Required text fields
    if (!campaign.product_name?.trim()) {
      errors.push('Product Name is required');
    }
    if (!campaign.id && !campaign.product_id?.trim()) {
      errors.push('Product ID is required');
    }
    if (!campaign.description?.trim()) {
      errors.push('Description is required');
    }
    if (!campaign.product_image_url?.trim()) {
      errors.push('Product Image is required');
    }

    // TAP Link validation
    if (!campaign.tap_link?.trim()) {
      errors.push('TAP Link is required');
    } else {
      try {
  let link = campaign.tap_link?.trim();

  if (!/^https?:\/\//i.test(link)) {
    link = 'https://' + link;
  }

  const url = new URL(link);

  // Ensure it has a valid domain (contains a dot)
  if (!url.hostname.includes('.')) {
    throw new Error('Invalid domain');
  }

} catch {
  errors.push('TAP Link must be a valid URL');
}
    }

    // Prize pool, slots, cash prizes, commission, min videos — not applicable for BMC Max
    if (!campaign.is_bmc_max) {
      const prizePool = Number(campaign.prize_pool);
      if (isNaN(prizePool) || prizePool < 1) {
        errors.push('Prize Pool must be 1 or greater');
      }

      const totalSlots = Number(campaign.total_video_slots);
      if (isNaN(totalSlots) || totalSlots < 1) {
        errors.push('Total Video Slots must be 1 or greater');
      }

      const rawPrizes: { position: number; amount: number }[] = (
        Array.isArray(campaign?.cash_prizes) ? campaign?.cash_prizes : []
      ).map((p: any) => ({ position: Number(p?.position), amount: Number(p?.amount) }));

      if (prizePool > 0 && rawPrizes.length === 0) {
        errors.push('At least one cash prize is required when a Prize Pool is set');
      }

      rawPrizes.forEach((p, i) => {
        if (!p.amount || p.amount < 1) errors.push(`Prize ${i + 1}: amount must be $1 or greater`);
      });

      if (rawPrizes.length > 0) {
        const prizeSum = rawPrizes.reduce((a, p) => a + p.amount, 0);
        if (prizeSum !== prizePool) {
          errors.push(`Prize sum ($${prizeSum.toLocaleString()}) must equal Prize Pool ($${prizePool.toLocaleString()})`);
        }
      }

      if (campaign.commission !== '' && campaign.commission != null) {
        const commission = Number(campaign.commission);
        if (isNaN(commission) || commission < 1) {
          errors.push('Commission must be 1 or greater');
        }
      }

      if (campaign.min_videos !== '' && campaign.min_videos != null) {
        const minVids = Number(campaign.min_videos);
        if (isNaN(minVids) || minVids < 5) {
          errors.push('Min Videos must be 5 or greater');
        }
      }
    }

    // Hybrid campaign validation
    if (campaign.is_hybrid) {
      if (!Array.isArray(campaign.gmv_ranges) || campaign.gmv_ranges.length === 0) {
        errors.push('At least one GMV range must be selected for hybrid campaigns');
      }
      if (!Array.isArray(campaign.genders) || campaign.genders.length === 0) {
        errors.push('At least one gender must be selected for hybrid campaigns');
      }
      const gp = Number(campaign.guaranteed_payment);
      if (isNaN(gp) || gp < 1) {
        errors.push('Guaranteed Payment must be $1 or greater for hybrid campaigns');
      }
    }

    // BMC Max campaign validation
    if (campaign.is_bmc_max) {
      const rv = Number(campaign.max_required_videos);
      if (isNaN(rv) || rv < 1) {
        errors.push('Required Videos must be 1 or greater for BMC Max campaigns');
      }
      const ra = Number(campaign.guaranteed_payment);
      if (isNaN(ra) || ra < 1) {
        errors.push('Reward Amount must be $1 or greater for BMC Max campaigns');
      }
      if (!Array.isArray(campaign.gmv_ranges) || campaign.gmv_ranges.length === 0) {
        errors.push('At least one GMV range must be selected for BMC Max campaigns');
      }
      if (!Array.isArray(campaign.genders) || campaign.genders.length === 0) {
        errors.push('At least one gender must be selected for BMC Max campaigns');
      }
    }

    // End date is required
    if (!campaign.end_date) {
      errors.push('Campaign Deadline is required');
    } else {
      const end = new Date(campaign.end_date);
      if (isNaN(end.getTime())) {
        errors.push('Campaign Deadline must be a valid date');
      } else if (campaign.status === 'active') {
        // Past-deadline is only invalid for active campaigns — paused/completed
        // campaigns are expected to have deadlines that have already passed.
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (end < today) errors.push('Campaign Deadline cannot be in the past');
      }
    }

    return { valid: errors.length === 0, errors };
  };

  const handleSave = async () => {
    if (!editing || saveInFlight.current) return;
    saveInFlight.current = true;

    // Guard: product_id must not change on edit
    if (editing.id && editing.product_id !== originalProductId.current) {
      toast.error('Product ID cannot be changed after a campaign is created');
      saveInFlight.current = false;
      return;
    }

    // Validate campaign fields
    const campaignValidation = validateCampaign(editing);
    if (!campaignValidation.valid) {
      campaignValidation.errors.forEach(error => toast.error(error));
      saveInFlight.current = false;
      return;
    }

    // Check for duplicate product_id across other campaigns (create only)
    if (!editing.id && editing.product_id?.trim()) {
      const { data: existing, error: dupError } = await supabase
        .from('campaigns')
        .select('id, product_name')
        .eq('product_id', editing.product_id.trim())
        .limit(1);
      if (dupError) {
        toast.error(dupError.message);
        saveInFlight.current = false;
        return;
      }
      if (existing && existing.length > 0) {
        toast.error(`Product ID already used by campaign "${existing[0].product_name}"`);
        saveInFlight.current = false;
        return;
      }
    }

    // Validate briefs
    const { valid, briefs: validated } = validateBriefs(briefs);
    if (!valid) {
      setBriefs(validated);
      toast.error('Please fill in all required brief fields');
      saveInFlight.current = false;
      return;
    }

    saveMutation.mutate({ campaign: editing, briefsToSave: briefs });
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Fetch old data for audit log
      const { data: oldData } = await supabase.from('campaigns').select('*').eq('id', id).single();
      
      await supabase.from('campaign_briefs').delete().eq('campaign_id', id);
      const { error } = await supabase.from('campaigns').delete().eq('id', id);
      if (error) throw error;

      // Audit log
      if (oldData) {
        await logDelete('campaigns', id, oldData, {
          description: `Deleted campaign "${oldData?.product_name}"`,
          metadata: {
            brand_name: oldData?.brand_name,
            product_name: oldData?.product_name
          }
        });
      }
    },
    onSuccess: () => {
      toast.success('Campaign deleted');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const statusColor = (s: string) => {
    switch (s) {
      case 'active': return 'default';
      case 'paused': return 'secondary';
      case 'completed': return 'outline';
      default: return 'secondary';
    }
  };

  // Get column config by field name
  const getColumnConfig = (field: string) => {
    return COLUMN_CONFIG.find(col => col.field === field);
  };

  // Handle sort - uses database field names
  const handleSort = (field: string) => {
    const columnConfig = getColumnConfig(field);
    // Only sort if column is sortable
    if (!columnConfig?.sortable) return;
    
    setCurrentPage(1);
    setSortConfig((current) => {
      if (!current || current.key !== field || current?.direction === 'desc') {
        return { key: field, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key: field, direction: 'desc' };
      }
      return null;
    });
  };

  const handlePageSizeChange = (newSize: number) => {
    setCurrentPage(1);
    setPageSize(newSize);
  };

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Get sort icon for column
  const getSortIcon = (field: string) => {
    if (!sortConfig || sortConfig.key !== field) {
      return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  const totalPages = Math.ceil((data?.total ?? 0) / pageSize);

  // Current campaign type derived from the two boolean flags (kept in the DB
  // for back-compat). The UI presents these as one mutually-exclusive choice.
  const campaignType: CampaignType = editing?.is_bmc_max ? 'bmc_max' : editing?.is_hybrid ? 'hybrid' : 'reward_only';

  // Switch campaign type, resetting the fields that don't belong to the new
  // mode. `guaranteed_payment` is reused by both Hybrid (guaranteed pay) and
  // BMC Max (reward amount), so it's always cleared on a type change to avoid
  // carrying a value across two different meanings.
  const selectCampaignType = async (type: CampaignType) => {
    if (!editing || typeCheckLoading) return;

    // No-op if type hasn't changed
    const currentType: CampaignType = editing.is_bmc_max ? 'bmc_max' : editing.is_hybrid ? 'hybrid' : 'reward_only';
    if (type === currentType) return;

    // Only applies when editing an existing campaign
    if (editing.id) {
      const origType = originalCampaignType.current;

      // Rule 1: reward_only campaigns can only be promoted to hybrid, never to bmc_max
      if (origType === 'reward_only' && type === 'bmc_max') {
        toast.error('A reward-only campaign can only be changed to Hybrid, not BMC Max.');
        return;
      }

      // Rule 2: hybrid / bmc_max campaigns cannot change type if any creator has joined or submitted.
      // All three checks run in parallel; each resolves independently even if one errors.
      if (origType === 'hybrid' || origType === 'bmc_max') {
        setTypeCheckLoading(true);
        try {
          const [
            { data: participants, error: partErr },
            { data: progress, error: progErr },
            { data: subs, error: subsErr },
          ] = await Promise.all([
            supabase.from('campaign_participants').select('id').eq('campaign_id', editing.id).limit(1),
            supabase.from('hybrid_campaign_progress').select('id').eq('campaign_id', editing.id).limit(1),
            supabase.from('hybrid_video_submissions').select('id').eq('campaign_id', editing.id).limit(1),
          ]);

          if (partErr || progErr || subsErr) {
            toast.error('Could not fully verify campaign activity — please try again.');
            return;
          }
          if (participants && participants.length > 0) {
            toast.error('This campaign already has participants. Its type cannot be changed.');
            return;
          }
          if (progress && progress.length > 0) {
            toast.error('This campaign has creator progress records. Its type cannot be changed.');
            return;
          }
          if (subs && subs.length > 0) {
            toast.error('This campaign has creator video submissions. Its type cannot be changed.');
            return;
          }
        } finally {
          setTypeCheckLoading(false);
        }
      }
    }

    const emptyNum = (v: any) => v === '' || v == null;
    const base = { ...editing, guaranteed_payment: '' };
    if (type === 'reward_only') {
      setEditing({
        ...base,
        is_hybrid: false,
        is_bmc_max: false,
        gmv_ranges: [],
        genders: [],
        max_required_videos: '',
        total_video_slots: emptyNum(editing.total_video_slots) ? 250 : editing.total_video_slots,
        min_videos: emptyNum(editing.min_videos) ? 5 : editing.min_videos,
      });
    } else if (type === 'hybrid') {
      setEditing({
        ...base,
        is_hybrid: true,
        is_bmc_max: false,
        max_required_videos: '',
        total_video_slots: emptyNum(editing.total_video_slots) ? 250 : editing.total_video_slots,
        min_videos: emptyNum(editing.min_videos) ? 5 : editing.min_videos,
      });
    } else {
      // BMC Max: no reward pool, slots, cash prizes, commission, or min videos.
      setEditing({
        ...base,
        is_bmc_max: true,
        is_hybrid: false,
        prize_pool: 0,
        cash_prizes: [],
        commission: '',
        min_videos: 5,
        total_video_slots: '',
      });
    }
  };

  const handleExportCampaigns = async () => {
    setIsExporting(true);
    try {
      let query = supabase
        .from('campaigns')
        .select('*');

      if (search && search.trim() !== '') {
        query = query.or(`brand_name.ilike.%${search}%,product_name.ilike.%${search}%`);
      }

      if (sortConfig) {
        query = query.order(sortConfig.key, {
          ascending: sortConfig.direction === 'asc',
        });
      } else {
        query = query
          .order('status', { ascending: true })
          .order('sort_order', { ascending: true, nullsFirst: false });
      }

      const { data: exportData, error } = await query;

      if (error) throw error;
      if (!exportData || exportData.length === 0) {
        toast.error('No campaigns available to export');
        return;
      }

      const columns: string[] = Array.from(
        exportData.reduce((keys, row) => {
          Object.keys(row).forEach((key) => keys.add(key));
          return keys;
        }, new Set<string>())
      );

      const escapeCsvValue = (value: any) => {
        const stringValue = value == null
          ? ''
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      };

      const csvRows = [
        columns.join(','),
        ...exportData.map((row) => columns.map((column) => escapeCsvValue(row[column])).join(',')),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `creator-campaigns-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportData?.length} campaigns`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to export campaigns');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs value={statusTab} onValueChange={(v) => { setStatusTab(v as any); setCurrentPage(1); }}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="paused">Paused</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={enterReorderMode} disabled={isLoadingReorder}>
            <ListOrdered className="mr-2 h-4 w-4" />
            {isLoadingReorder ? 'Loading...' : 'Reorder'}
          </Button>
          <Button onClick={() => setEditing({ ...emptyCampaign })}><Plus className="mr-2 h-4 w-4" /> Add Campaign</Button>
        </div>
      </div>
      {/* ── Reorder Mode ─────────────────────────────────────────────────────── */}
      {reorderMode && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl sm:text-2xl">Reorder Campaigns</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Drag rows to set the priority order creators see campaigns in.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={() => setReorderMode(false)} disabled={saveOrderMutation.isPending}>
                  <X className="mr-2 h-4 w-4" /> Cancel
                </Button>
                <Button onClick={() => saveOrderMutation.mutate()} disabled={saveOrderMutation.isPending}>
                  <Check className="mr-2 h-4 w-4" />
                  {saveOrderMutation.isPending ? 'Saving...' : 'Save Order'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {reorderList.map((c, i) => (
                <div
                  key={c.id}
                  draggable
                  onDragStart={() => handleDragStart(i)}
                  onDragEnter={() => handleDragEnter(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 px-4 py-3 cursor-grab active:cursor-grabbing select-none transition-colors ${draggingIndex === i ? 'opacity-40 bg-muted' : 'hover:bg-muted/50'}`}
                >
                  <span className="text-xs text-muted-foreground w-6 text-right shrink-0">{i + 1}</span>
                  <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                  {c.product_image_url && (
                    <img src={c.product_image_url} alt="" className="h-8 w-8 rounded object-cover shrink-0 border" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.brand_name} — {c.product_name}</p>
                  </div>
                  <Badge variant={statusColor(c.status)} className="shrink-0">{c.status}</Badge>
                  {c.is_hybrid
                    ? <Badge className="text-xs bg-emerald-600 hover:bg-emerald-600 text-white shrink-0">Hybrid</Badge>
                    : c.is_bmc_max
                      ? <Badge className="text-xs bg-purple-600 hover:bg-purple-600 text-white shrink-0">Pay per {c.max_required_videos ?? 10} Posts</Badge>
                      : <Badge className="text-xs bg-blue-600 hover:bg-blue-600 text-white shrink-0">Reward Only</Badge>
                  }
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Normal Table ──────────────────────────────────────────────────────── */}
      {!reorderMode && <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-xl sm:text-2xl">Campaigns ({data?.total ?? 0})</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={handleExportCampaigns} disabled={isExporting || !data?.data?.length}>
                <Download className="mr-2 h-4 w-4" />
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
              <div className="relative w-full sm:w-auto">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search campaigns..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-8 w-full sm:w-[300px]"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Cells are nowrap by default (ui/table.tsx). The GMV Ranges and
              Genders cells stack via flex-wrap, which nowrap does not affect,
              so their badges still lay out as before. */}
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMN_CONFIG?.map((column) => (
                  <TableHead key={column?.field} className="whitespace-nowrap">
                    {column.sortable ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8 data-[state=open]:bg-accent"
                        onClick={() => handleSort(column?.field)}
                      >
                        <span>{column?.label}</span>
                        {getSortIcon(column?.field)}
                      </Button>
                    ) : (
                      // No px-3 — it would offset this label 12px from the
                      // cell text below (the sortable Button cancels its own).
                      <span>{column?.label}</span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                </TableRow>
              )}
              {!isLoading && (!data?.data || data?.data?.length === 0) && (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">No campaigns found</TableCell>
                </TableRow>
              )}
              {data?.data?.map((c: any) => (
                <TableRow key={c.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setViewing(c)}>
                  <TableCell className="font-medium max-w-[140px]">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="block truncate cursor-default">{c.brand_name}</span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[300px] whitespace-normal break-words">{c.brand_name}</TooltipContent>
                    </Tooltip>
                  </TableCell>
                  <TableCell className="max-w-[200px]">
                    <div className="flex items-center gap-2 min-w-0">
                      {c.product_image_url && <img src={c.product_image_url} alt="" className="h-6 w-6 rounded object-cover shrink-0" />}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="truncate cursor-default">{c.product_name}</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[300px] whitespace-normal break-words">{c.product_name}</TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {c.is_hybrid
                      ? <Badge className="text-xs bg-emerald-600 hover:bg-emerald-600 text-white">Hybrid</Badge>
                      : c.is_bmc_max
                        ? <Badge className="text-xs bg-purple-600 hover:bg-purple-600 text-white">Pay per {c.max_required_videos ?? 10} Posts</Badge>
                        : <Badge className="text-xs bg-blue-600 hover:bg-blue-600 text-white">Reward Only</Badge>
                    }
                  </TableCell>
                  <TableCell className="whitespace-nowrap">${Number(c.prize_pool || 0).toLocaleString()}</TableCell>
                  <TableCell className="whitespace-nowrap">{c.commission != null ? `${c.commission}%` : '—'}</TableCell>
                  {/* <TableCell>{c.gems_coin != null ? c.gems_coin : '—'}</TableCell> */}
                  <TableCell className="whitespace-nowrap"> {c.is_bmc_max ? '_' : `${c.videos_submitted ?? 0}/${c.total_video_slots ?? 250}` } </TableCell>
                  <TableCell>{c.min_videos || c.max_required_videos ||  5}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {(c.is_hybrid || c.is_bmc_max) && c.guaranteed_payment != null
                      ? <span className="text-emerald-600 font-medium">${Number(c.guaranteed_payment).toLocaleString()}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    {c.end_date ? (() => {
                      const end = new Date(c.end_date);
                      const now = new Date();
                      const daysLeft = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                      const label = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                      if (daysLeft < 0) return <span className="text-xs text-destructive font-medium">{label} (expired)</span>;
                      if (daysLeft <= 7) return <span className="text-xs text-amber-600 font-medium">{label} ({daysLeft}d left)</span>;
                      return <span className="text-xs text-muted-foreground">{label}</span>;
                    })() : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="max-w-[180px]">
                    {(() => {
                      const ranges: string[] = c.gmv_ranges ?? [];
                      if (!ranges.length) return <span className="text-muted-foreground text-xs">—</span>;
                      if (ranges.length === GMV_RANGES.length) return <Badge variant="outline" className="text-xs">All</Badge>;
                      const labels = ranges.map(k => GMV_RANGES.find(r => r.key === k)?.label ?? k);
                      const visible = labels.slice(0, 2);
                      const hidden = labels.slice(2);
                      return (
                        <div className="flex flex-wrap gap-1 items-center">
                          {visible.map(l => (
                            <Badge key={l} variant="outline" className="text-xs whitespace-nowrap">{l}</Badge>
                          ))}
                          {hidden.length > 0 && (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="secondary" className="text-xs cursor-default">+{hidden.length}</Badge>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[220px]">
                                  <div className="flex flex-col gap-1">
                                    {hidden.map(l => <span key={l} className="text-xs">{l}</span>)}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="max-w-[140px]">
                    {(() => {
                      const genders: string[] = c.genders ?? [];
                      if (!genders.length) return <span className="text-muted-foreground text-xs">—</span>;
                      if (genders.length === GENDER_OPTIONS.length) return <Badge variant="outline" className="text-xs">All</Badge>;
                      return (
                        <div className="flex flex-wrap gap-1">
                          {genders.map(g => (
                            <Badge key={g} variant="outline" className="text-xs whitespace-nowrap">{GENDER_OPTIONS.find(o => o.key === g)?.label ?? g}</Badge>
                          ))}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell><Badge variant={statusColor(c?.status)}>{c?.status}</Badge></TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => {
                        originalProductId.current = c?.product_id ?? null;
                        originalCampaignType.current = c?.is_bmc_max ? 'bmc_max' : c?.is_hybrid ? 'hybrid' : 'reward_only';
                        // Normalise cash_prizes to {position, amount} objects
                        let prizes: { position: number; amount: string }[] = [];
                        if (Array.isArray(c?.cash_prizes) && c?.cash_prizes?.length > 0) {
                          prizes = c?.cash_prizes?.map((p: any) =>
                            typeof p === 'object' && p !== null
                              ? { position: Number(p.position), amount: String(p.amount) }
                              : { position: 0, amount: String(p) }   // legacy flat-number fallback
                          );
                        } else {
                          prizes = [c?.['1st_prize'], c?.['2nd_prize'], c?.['3rd_prize']]
                            .filter(v => v != null && Number(v) > 0)
                            .map((v, i) => ({ position: i + 1, amount: String(v) }));
                        }
                        setEditing({ ...c, cash_prizes: prizes, gmv_ranges: c.gmv_ranges ?? [], genders: c.genders ?? [], additional_image_urls: c.additional_image_urls ?? [] });
                      }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete campaign?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteMutation.mutate(c.id)}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {data?.data && data?.data?.length > 0 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={data?.total ?? 0}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>}

      {/* Campaign Detail Sheet (read-only) */}
      <Sheet open={!!viewing} onOpenChange={(open) => { if (!open) setViewing(null); }}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between pr-6">
              <span>Campaign Details</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!viewing) return;
                  let prizes: { position: number; amount: string }[] = [];
                  if (Array.isArray(viewing?.cash_prizes) && viewing?.cash_prizes?.length > 0) {
                    prizes = viewing.cash_prizes.map((p: any) =>
                      typeof p === 'object' && p !== null
                        ? { position: Number(p.position), amount: String(p.amount) }
                        : { position: 0, amount: String(p) }
                    );
                  } else {
                    prizes = [viewing?.['1st_prize'], viewing?.['2nd_prize'], viewing?.['3rd_prize']]
                      .filter(v => v != null && Number(v) > 0)
                      .map((v, i) => ({ position: i + 1, amount: String(v) }));
                  }
                  originalProductId.current = viewing?.product_id ?? null;
                  originalCampaignType.current = viewing?.is_bmc_max ? 'bmc_max' : viewing?.is_hybrid ? 'hybrid' : 'reward_only';
                  setViewing(null);
                  setEditing({ ...viewing, cash_prizes: prizes, gmv_ranges: viewing.gmv_ranges ?? [], genders: viewing.genders ?? [], is_bmc_max: viewing.is_bmc_max ?? false, additional_image_urls: viewing.additional_image_urls ?? [] });
                }}
              >
                <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
            </SheetTitle>
          </SheetHeader>
          {viewing && (
            <div className="space-y-5 mt-6">
              {/* Product image + names */}
              <div className="flex items-start gap-4">
                {viewing.product_image_url && (
                  <img src={viewing.product_image_url} alt="Product" className="h-20 w-20 rounded-lg border object-cover shrink-0" />
                )}
                <div className="space-y-1 min-w-0">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Brand</p>
                  <p className="font-semibold text-base">{viewing.brand_name || '—'}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mt-2">Product</p>
                  <p className="font-medium">{viewing.product_name || '—'}</p>
                  <Badge variant={statusColor(viewing.status)} className="mt-1">{viewing.status}</Badge>
                </div>
              </div>

              {/* Additional product images */}
              {Array.isArray(viewing.additional_image_urls) && viewing.additional_image_urls.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Additional Images</p>
                  <div className="flex flex-wrap gap-2">
                    {viewing.additional_image_urls.map((url: string, idx: number) => (
                      <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt={`Additional ${idx + 1}`} className="h-16 w-16 rounded border object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Key metrics */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-xs text-muted-foreground">Prize Pool</p>
                  <p className="font-bold text-lg">${Number(viewing.prize_pool || 0).toLocaleString()}</p>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-xs text-muted-foreground">Commission</p>
                  <p className="font-bold text-lg">{viewing.commission != null ? `${viewing.commission}%` : '—'}</p>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-xs text-muted-foreground">Slots</p>
                  <p className="font-bold text-lg">{viewing.videos_submitted ?? 0}/{viewing.total_video_slots ?? 250}</p>
                </div>
              </div>

              {/* Hybrid Campaign Banner */}
              {viewing.is_hybrid && (
                <div className="rounded-lg border border-emerald-600/30 bg-emerald-50 dark:bg-emerald-950/20 p-3 flex items-start gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Hybrid Campaign</p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-0.5">
                      Creators earn both the prize pool reward and a guaranteed payment of{' '}
                      <span className="font-bold">${Number(viewing.guaranteed_payment || 0).toLocaleString()}</span>
                      {' '}for completing 10 videos.
                    </p>
                  </div>
                </div>
              )}

              {/* BMC Max Campaign Banner */}
              {viewing.is_bmc_max && (
                <div className="rounded-lg border border-purple-600/30 bg-purple-50 dark:bg-purple-950/20 p-3 flex items-start gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-purple-700 dark:text-purple-400">BMC Max Campaign</p>
                    <p className="text-xs text-purple-600 dark:text-purple-500 mt-0.5">
                      Requires{' '}
                      <span className="font-bold">{viewing.max_required_videos ?? viewing.min_videos ?? 5} videos</span>
                      {' '}to qualify.
                      {viewing.guaranteed_payment != null && (
                        <>{' '}Reward: <span className="font-bold">${Number(viewing.guaranteed_payment).toLocaleString()}</span></>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Details */}
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Product ID</p>
                  <p className="text-sm font-mono bg-muted rounded px-2 py-1">{viewing.product_id || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">TAP Link</p>
                  {viewing.tap_link ? (
                    <a
                      href={viewing.tap_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary underline flex items-center gap-1 break-all"
                    >
                      {viewing.tap_link}
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  ) : <p className="text-sm">—</p>}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Description</p>
                  <p className="text-sm whitespace-pre-wrap">{viewing.description || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Min Videos</p>
                  <p className="text-sm">{viewing.min_videos || viewing.max_required_videos || 5}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">GMV Range Targeting</p>
                  {(() => {
                    const ranges: string[] = viewing.gmv_ranges ?? [];
                    if (!ranges.length) return <p className="text-sm text-muted-foreground">—</p>;
                    if (ranges.length === GMV_RANGES.length) {
                      return <Badge variant="outline" className="text-xs">All ranges</Badge>;
                    }
                    return (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {ranges.map(k => {
                          const label = GMV_RANGES.find(r => r.key === k)?.label ?? k;
                          return <Badge key={k} variant="outline" className="text-xs">{label}</Badge>;
                        })}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Gender Targeting</p>
                  {(() => {
                    const genders: string[] = viewing.genders ?? [];
                    if (!genders.length) return <p className="text-sm text-muted-foreground">—</p>;
                    if (genders.length === GENDER_OPTIONS.length) {
                      return <Badge variant="outline" className="text-xs">All genders</Badge>;
                    }
                    return (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {genders.map(k => {
                          const label = GENDER_OPTIONS.find(o => o.key === k)?.label ?? k;
                          return <Badge key={k} variant="outline" className="text-xs">{label}</Badge>;
                        })}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Campaign Deadline</p>
                  {viewing.end_date ? (() => {
                    const end = new Date(viewing.end_date);
                    const now = new Date();
                    const daysLeft = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    const label = end.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                    const startLabel = viewing.created_at
                      ? new Date(viewing.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : null;
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          {startLabel && (
                            <>
                              <span className="text-muted-foreground">{startLabel}</span>
                              <span className="text-muted-foreground">→</span>
                            </>
                          )}
                          <span className={daysLeft < 0 ? 'text-destructive font-medium' : daysLeft <= 7 ? 'text-amber-600 font-medium' : 'font-medium'}>{label}</span>
                        </div>
                        <p className={`text-xs ${daysLeft < 0 ? 'text-destructive' : daysLeft <= 7 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                          {daysLeft < 0 ? `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago` : daysLeft === 0 ? 'Expires today' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`}
                        </p>
                      </div>
                    );
                  })() : <p className="text-sm text-muted-foreground">No deadline set</p>}
                </div>
              </div>

              {/* Cash prizes */}
              {(() => {
                let prizes: { position: number; amount: number }[] = [];
                if (Array.isArray(viewing?.cash_prizes) && viewing.cash_prizes.length > 0) {
                  prizes = viewing.cash_prizes.map((p: any) =>
                    typeof p === 'object' ? { position: Number(p.position), amount: Number(p.amount) } : { position: 0, amount: Number(p) }
                  );
                } else {
                  prizes = [viewing?.['1st_prize'], viewing?.['2nd_prize'], viewing?.['3rd_prize']]
                    .filter(v => v != null && Number(v) > 0)
                    .map((v, i) => ({ position: i + 1, amount: Number(v) }));
                }
                const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];
                if (prizes.length === 0) return null;
                return (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Cash Prizes</p>
                    <div className="space-y-1">
                      {prizes.sort((a, b) => a.position - b.position).map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-sm px-2 py-1 rounded bg-muted/50">
                          <span className="text-muted-foreground">{ordinals[p.position - 1] ?? `${p.position}th`} Place</span>
                          <span className="font-medium">${Number(p.amount).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Video Briefs */}
              {viewBriefs.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Video Briefs ({viewBriefs.length})</p>
                  <div className="space-y-3">
                    {viewBriefs.map((b, i) => (
                      <div key={i} className="rounded-lg border p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs capitalize">{b.brief_type}</Badge>
                          {b.title && <span className="font-medium text-sm">{b.title}</span>}
                        </div>
                        {b.description && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{b.description}</p>}
                        {b.thumbnail_url && (
                          <img src={b.thumbnail_url} alt="Brief thumbnail" className="h-24 w-auto rounded border object-cover" />
                        )}
                        {b.example_video_url && (
                          <a href={b.example_video_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" /> Example video
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Created date */}
              {viewing.created_at && (
                <p className="text-xs text-muted-foreground">
                  Created {new Date(viewing.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={!!editing} onOpenChange={(open) => { if (!open) { setEditing(null); setBriefs([]); } }}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-lg">
          <SheetHeader><SheetTitle>{editing?.id ? 'Edit' : 'New'} Campaign</SheetTitle></SheetHeader>
          {editing && (
            <div className="space-y-4 mt-6">
              <div>
                <Label>Brand Name</Label>
                <Input maxLength={60} value={editing.brand_name} onChange={e => setEditing({ ...editing, brand_name: e.target.value })} />
                <p className="text-xs text-muted-foreground mt-1 text-right">{(editing.brand_name || '').length}/60</p>
              </div>
              <div>
                <Label>Product Name <span className="text-destructive">*</span></Label>
                <Input maxLength={100} value={editing.product_name} onChange={e => setEditing({ ...editing, product_name: e.target.value })} />
                <p className="text-xs text-muted-foreground mt-1 text-right">{(editing.product_name || '').length}/100</p>
              </div>
              <div><Label>Product ID {!editing?.id && <span className="text-destructive">*</span>}</Label><Input value={editing.product_id || ''} onChange={e => setEditing({ ...editing, product_id: e.target.value })} placeholder="TikTok Product ID" disabled={!!editing?.id} /></div>
              <div><Label>TAP Link <span className="text-destructive">*</span></Label><Input value={editing.tap_link || ''} onChange={e => setEditing({ ...editing, tap_link: e.target.value })} placeholder="https://affiliate.tiktok.com/..." /></div>

              <div className="space-y-2">
                <Label>Product Image <span className="text-destructive">*</span></Label>
                <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                <div className="flex items-center gap-3">
                  <Button type="button" variant="outline" size="sm" disabled={uploadingPhoto} onClick={() => photoInputRef.current?.click()}>
                    <Upload className="mr-2 h-4 w-4" />{uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                  {editing.product_image_url && (
                    <a href={editing.product_image_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline flex items-center gap-1">
                      <Image className="h-3 w-3" /> View current
                    </a>
                  )}
                </div>
                {editing.product_image_url && (
                  <img src={editing.product_image_url} alt="Product" className="h-20 w-20 rounded border object-cover" />
                )}
              </div>

              <div className="space-y-2">
                <Label>Additional Product Images <span className="text-muted-foreground text-xs">(optional, up to {MAX_ADDITIONAL_IMAGES})</span></Label>
                <input ref={extraPhotoInputRef} type="file" accept="image/*" className="hidden" onChange={handleExtraPhotoUpload} />
                <div className="flex flex-wrap gap-2">
                  {(editing.additional_image_urls ?? []).map((url: string, idx: number) => (
                    <div key={idx} className="relative h-20 w-20 shrink-0">
                      <img src={url} alt={`Additional ${idx + 1}`} className="h-20 w-20 rounded border object-cover" />
                      <button
                        type="button"
                        onClick={() => removeExtraImage(idx)}
                        className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {(editing.additional_image_urls?.length ?? 0) < MAX_ADDITIONAL_IMAGES && (
                    <button
                      type="button"
                      disabled={uploadingExtraPhoto}
                      onClick={() => extraPhotoInputRef.current?.click()}
                      className="h-20 w-20 shrink-0 rounded border border-dashed flex flex-col items-center justify-center gap-1 text-muted-foreground hover:bg-muted/50 disabled:opacity-60"
                    >
                      <Upload className="h-4 w-4" />
                      <span className="text-xs">{uploadingExtraPhoto ? 'Uploading...' : 'Add'}</span>
                    </button>
                  )}
                </div>
              </div>

              <div><Label>Description <span className="text-destructive">*</span></Label><Textarea value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} rows={3} /></div>

              {/* Campaign Type — one mutually-exclusive choice. Each type's
                  fields live in a color-matched panel below, so it's obvious
                  what the choice controls. */}
              <div className="space-y-2">
                <Label>Campaign Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {CAMPAIGN_TYPES.map(t => {
                    const selected = campaignType === t.key;
                    const checking = typeCheckLoading && selected;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => selectCampaignType(t.key)}
                        disabled={typeCheckLoading}
                        className={`rounded-lg border p-3 text-left transition-colors ${selected ? t.activeClass : 'hover:bg-muted/50'} ${typeCheckLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          {checking
                            ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
                            : <span className={`h-2.5 w-2.5 rounded-full ${t.dot}`} />
                          }
                          <span className="text-sm font-medium">{t.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{t.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Hybrid settings — emerald, matches the Hybrid badge. Everything
                  Hybrid adds on top of the Reward Pool lives here. */}
              {editing.is_hybrid && (
                <div className="rounded-lg border border-emerald-600/40 bg-emerald-50 dark:bg-emerald-950/20 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Hybrid settings</p>
                  </div>
                  <p className="text-xs text-emerald-700/80 dark:text-emerald-500/80">
                    Creators earn the Reward Pool below <span className="font-medium">and</span> a guaranteed payment for completing {editing.min_videos ?? 10} videos.
                  </p>
                  <div>
                    <Label>Min Videos <span className="text-muted-foreground text-xs">(minimum 5)</span></Label>
                    <Input type="number" min="5" value={editing?.min_videos ?? 5} onChange={e => setEditing({ ...editing, min_videos: e.target.value })} placeholder="5" />
                  </div>
                  <div>
                    <Label>Guaranteed Payment ($) <span className="text-destructive">*</span></Label>
                    <Input
                      type="number"
                      min="1"
                      value={editing.guaranteed_payment || ''}
                      onChange={e => setEditing({ ...editing, guaranteed_payment: e.target.value })}
                      placeholder="e.g. 200"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Fixed payout for completing all {editing.min_videos ?? 10} videos</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>GMV range that will see the paid job <span className="text-destructive">*</span></Label>
                    <TargetMultiSelect options={GMV_RANGES} value={editing.gmv_ranges ?? []} onChange={next => setEditing({ ...editing, gmv_ranges: next })} noun="GMV ranges" />
                    {/* <p className="text-xs text-muted-foreground">All creators can still see the leaderboard.</p> */}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Genders that will see the paid job <span className="text-destructive">*</span></Label>
                    <TargetMultiSelect options={GENDER_OPTIONS} value={editing.genders ?? []} onChange={next => setEditing({ ...editing, genders: next })} noun="genders" />
                    <p className="text-xs text-muted-foreground">Only creators of the selected genders see the paid job.</p>
                  </div>
                </div>
              )}

              {/* BMC Max settings — purple, matches the BMC Max badge. BMC Max
                  replaces the Reward Pool entirely, so all its fields live here. */}
              {editing.is_bmc_max && (
                <div className="rounded-lg border border-purple-600/40 bg-purple-50 dark:bg-purple-950/20 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-purple-600" />
                    <p className="text-sm font-semibold text-purple-700 dark:text-purple-400">BMC Max settings</p>
                  </div>
                  <p className="text-xs text-purple-700/80 dark:text-purple-500/80">
                    A fixed reward for a custom number of videos. No prize pool, cash prizes, commission, or slots.
                  </p>
                  <div>
                    <Label>Required Videos <span className="text-destructive">*</span></Label>
                    <Input
                      type="number"
                      min="1"
                      value={editing.max_required_videos || ''}
                      onChange={e => setEditing({ ...editing, max_required_videos: e.target.value })}
                      placeholder="e.g. 10"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Number of videos the creator must submit to qualify</p>
                  </div>
                  <div>
                    <Label>Reward Amount ($) <span className="text-destructive">*</span></Label>
                    <Input
                      type="number"
                      min="1"
                      value={editing.guaranteed_payment || ''}
                      onChange={e => setEditing({ ...editing, guaranteed_payment: e.target.value })}
                      placeholder="e.g. 500"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Total payout paid to the creator for completing all required videos</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>GMV range that will see the paid job <span className="text-destructive">*</span></Label>
                    <TargetMultiSelect options={GMV_RANGES} value={editing.gmv_ranges ?? []} onChange={next => setEditing({ ...editing, gmv_ranges: next })} noun="GMV ranges" />
                    {/* <p className="text-xs text-muted-foreground">All creators can still see the leaderboard.</p> */}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Genders that will see the paid job <span className="text-destructive">*</span></Label>
                    <TargetMultiSelect options={GENDER_OPTIONS} value={editing.genders ?? []} onChange={next => setEditing({ ...editing, genders: next })} noun="genders" />
                    <p className="text-xs text-muted-foreground">Only creators of the selected genders see the paid job.</p>
                  </div>
                </div>
              )}

              {!editing.is_bmc_max && (
              <div className="rounded-lg border p-4 space-y-4">
              <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${editing.is_hybrid ? 'bg-emerald-600' : 'bg-blue-600'}`} />
                <p className="text-sm font-semibold">Reward Pool</p>
              </div>
              {editing.is_hybrid && (
                <p className="text-xs text-muted-foreground -mt-2">Paid out in addition to the guaranteed payment above.</p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Prize Pool ($)</Label><Input type="number" min="1" value={editing.prize_pool} onChange={e => setEditing({ ...editing, prize_pool: e.target.value })} /></div>
                <div><Label>Total Video Slots</Label><Input type="number" min="1" value={editing.total_video_slots} onChange={e => setEditing({ ...editing, total_video_slots: e.target.value })} /></div>
              </div>

              {/* Dynamic cash prizes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Cash Prizes <span className="text-destructive">*</span> <span className="text-muted-foreground text-xs">(sum must equal Prize Pool)</span></Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const nextPos = (editing.cash_prizes ?? []).length + 1;
                      setEditing({ ...editing, cash_prizes: [...(editing.cash_prizes ?? []), { position: nextPos, amount: '' }] });
                    }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Prize
                  </Button>
                </div>
                {(!editing?.cash_prizes || editing?.cash_prizes.length === 0) && (
                  <p className="text-xs text-destructive py-1">No prizes added. At least one prize is required when a Prize Pool is set.</p>
                )}
                <div className="space-y-2">
                  {(editing?.cash_prizes ?? []).map((prize: any, i: number) => {
                    const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];
                    const label = ordinals[i] ?? `${i + 1}th`;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground w-8 shrink-0 text-right">{label}</span>
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                          <Input
                            type="number"
                            min="1"
                            className="pl-6"
                            placeholder="Amount"
                            value={prize.amount}
                            onChange={e => {
                              const updated = [...(editing.cash_prizes ?? [])];
                              updated[i] = { position: i + 1, amount: e.target.value };
                              setEditing({ ...editing, cash_prizes: updated });
                            }}
                          />
                        </div>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => {
                            const updated = (editing.cash_prizes ?? [])
                              .filter((_: any, idx: number) => idx !== i)
                              .map((p: any, idx: number) => ({ ...p, position: idx + 1 }));
                            setEditing({ ...editing, cash_prizes: updated });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                  {(editing?.cash_prizes ?? []).length > 0 && (() => {
                    const sum = (editing?.cash_prizes ?? [])
                      .map((p: any) => Number(p?.amount))
                      .filter(v => !isNaN(v) && v > 0)
                      .reduce((a: number, b: number) => a + b, 0);
                    const pool = Number(editing?.prize_pool) || 0;
                    return (
                      <p className={`text-xs mt-1 ${sum === pool ? 'text-green-600' : 'text-amber-600'}`}>
                        Sum: ${sum.toLocaleString()} / Pool: ${pool.toLocaleString()}
                        {sum === pool ? ' ✓' : ` (${sum < pool ? '-' : '+'}$${Math.abs(pool - sum).toLocaleString()})`}
                      </p>
                    );
                  })()}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><Label>Commission (%)</Label><Input type="number" min="0" value={editing?.commission || ''} onChange={e => setEditing({ ...editing, commission: e.target.value })} /></div>
                {/* <div><Label>Gems Coins</Label><Input type="number" min="0" value={editing?.gems_coin || ''} onChange={e => setEditing({ ...editing, gems_coin: e.target.value })} placeholder="0" /></div> */}
              </div>
              {!editing.is_hybrid && (
                <div>
                  <Label>Min Videos <span className="text-muted-foreground text-xs">(minimum 5)</span></Label>
                  <Input type="number" min="5" value={editing?.min_videos ?? 5} onChange={e => setEditing({ ...editing, min_videos: e.target.value })} placeholder="5" />
                </div>
              )}
              </div>
              )}
              <div>
                <Label>Status</Label>
                <Select value={editing.status} onValueChange={v => setEditing({ ...editing, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Campaign Deadline <span className="text-destructive">*</span></Label>
                <Input
                  type="date"
                  min={editing.status === 'active' ? new Date().toISOString().slice(0, 10) : undefined}
                  value={editing.end_date ? editing.end_date.slice(0, 10) : ''}
                  onChange={e => setEditing({ ...editing, end_date: e.target.value || '' })}
                />
                {editing.end_date && (() => {
                  const end = new Date(editing.end_date);
                  const now = new Date();
                  const daysLeft = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                  // Past-deadline warning only applies to active campaigns — paused/completed
                  // campaigns are expected to have deadlines that have already passed.
                  if (daysLeft < 0) {
                    if (editing.status !== 'active') return null;
                    return <p className="text-xs text-destructive mt-1">This date is in the past</p>;
                  }
                  if (daysLeft <= 7) return <p className="text-xs text-amber-600 mt-1">{daysLeft} day{daysLeft !== 1 ? 's' : ''} from today</p>;
                  return <p className="text-xs text-muted-foreground mt-1">{daysLeft} days from today</p>;
                })()}
              </div>

              <VideoBriefs briefs={briefs} onChange={setBriefs} />

              <Button className="w-full" onClick={handleSave} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving...' : 'Save Campaign'}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
    </TooltipProvider>
  );
}
